import type { AgentDirectoryEntry } from '@/hooks/useAssignableAgents'
import type { MemberAssignmentSummary } from '@/lib/api/services/teamService'
import { hasInboxAccess } from '@/lib/inboxPermissions'
import { hasTicketAccess } from '@/lib/ticketPermissions'

const isInactive = (a: AgentDirectoryEntry) => String(a.status ?? 'active').trim().toLowerCase() === 'inactive'

function requiredWorkspaces(
  departing: AgentDirectoryEntry | undefined,
  summary: MemberAssignmentSummary | null | undefined,
): { ids: string[]; needsAll: boolean } {
  const fromWork = summary?.work_workspace_ids ?? []
  if (summary && !summary.scopes_incomplete && fromWork.length > 0) return { ids: fromWork, needsAll: false }
  if (departing?.spans_all_workspaces) return { ids: [], needsAll: true }
  const own = departing?.workspace_ids ?? []
  return { ids: Array.from(new Set([...fromWork, ...own])), needsAll: false }
}

export function eligibleReassignTargets(
  directory: Record<string, AgentDirectoryEntry>,
  memberId: string,
  summary: MemberAssignmentSummary | null | undefined,
): AgentDirectoryEntry[] {
  const departing = directory[memberId]
  const { ids: workspaceIds, needsAll } = requiredWorkspaces(departing, summary)
  const counts = summary?.counts
  const needsInbox = (counts?.conversations ?? 0) > 0 || (counts?.social_threads ?? 0) > 0
  const needsTickets = (counts?.tickets ?? 0) > 0

  return Object.values(directory)
    .filter((a) => {
      if (!a?.id || a.id === memberId || isInactive(a)) return false
      if (String(a.role ?? '').trim().toUpperCase() === 'RIDER') return false
      if (needsAll && !a.spans_all_workspaces) return false
      if (!a.spans_all_workspaces) {
        const own = new Set(a.workspace_ids ?? [])
        if (!workspaceIds.every((ws) => own.has(ws))) return false
      }
      if (needsInbox && !hasInboxAccess(a.menuPermissions ?? null)) return false
      if (needsTickets && !hasTicketAccess(a.menuPermissions ?? null)) return false
      return true
    })
    .sort((a, b) => String(a.name || a.email || '').localeCompare(String(b.name || b.email || ''), undefined, { sensitivity: 'base' }))
}

const isBaseEligible = (a: AgentDirectoryEntry | undefined, memberId: string): a is AgentDirectoryEntry =>
  !!a?.id && a.id !== memberId && !isInactive(a) && String(a.role ?? '').trim().toUpperCase() !== 'RIDER'

const byName = (a: AgentDirectoryEntry, b: AgentDirectoryEntry) =>
  String(a.name || a.email || '').localeCompare(String(b.name || b.email || ''), undefined, { sensitivity: 'base' })

export interface ReassignBucket {
  key: string
  workspaceId: string | null
  name: string | null
  counts: NonNullable<MemberAssignmentSummary['work_by_workspace']>[number]['counts']
  candidates: AgentDirectoryEntry[]
}

export interface ReassignPlan {
  split: boolean
  buckets: ReassignBucket[]
  candidates: AgentDirectoryEntry[]
}

export const bucketKey = (workspaceId: string | null) => workspaceId || '__unscoped__'

export function canTakeBucket(a: AgentDirectoryEntry, bucket: Pick<ReassignBucket, 'workspaceId' | 'counts'>): boolean {
  if (bucket.workspaceId && !a.spans_all_workspaces && !(a.workspace_ids ?? []).includes(bucket.workspaceId)) return false
  const c = bucket.counts || {}
  if (((c.conversations ?? 0) > 0 || (c.social_threads ?? 0) > 0) && !hasInboxAccess(a.menuPermissions ?? null)) return false
  if ((c.tickets ?? 0) > 0 && !hasTicketAccess(a.menuPermissions ?? null)) return false
  return true
}

export function buildReassignPlan(
  directory: Record<string, AgentDirectoryEntry>,
  memberId: string,
  summary: MemberAssignmentSummary | null | undefined,
): ReassignPlan {
  const rawBuckets = (summary?.work_by_workspace ?? []).filter((b) => (b?.total ?? 0) > 0)
  const canSplit = !!summary && !summary.scopes_incomplete && rawBuckets.length > 1 && rawBuckets.some((b) => !!b.workspace_id)
  if (!canSplit) {
    return { split: false, buckets: [], candidates: eligibleReassignTargets(directory, memberId, summary) }
  }

  const pool = Object.values(directory).filter((a): a is AgentDirectoryEntry => isBaseEligible(a, memberId))
  const buckets: ReassignBucket[] = rawBuckets
    .map((b) => ({
      key: bucketKey(b.workspace_id),
      workspaceId: b.workspace_id,
      name: b.name,
      counts: b.counts || {},
      candidates: [] as AgentDirectoryEntry[],
    }))
    .map((b) => ({ ...b, candidates: pool.filter((a) => canTakeBucket(a, b)).sort(byName) }))
    .sort((x, y) => (x.workspaceId ? 0 : 1) - (y.workspaceId ? 0 : 1) || String(x.name || '').localeCompare(String(y.name || ''), undefined, { sensitivity: 'base' }))

  const scoped = buckets.filter((b) => !!b.workspaceId)
  const candidates = pool
    .filter((a) => scoped.some((b) => b.candidates.some((c) => c.id === a.id)))
    .sort(byName)

  return { split: true, buckets, candidates }
}
